import {
  Box,
  Chip,
  alpha,
  Alert,
  Stack,
  Paper,
  Button,
  Drawer,
  Divider,
  Tooltip,
  useTheme,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import MultiSortRuleCard from './multi-sort-rule-card';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SortRoundedIcon from '@mui/icons-material/SortRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import type { MultiSortColumnOption, MultiSortDirection } from './model';
import type { DraftMultiSortRule, MultiSortFocusRequest } from './types';

type MultiSortPanelProps = {
  open: boolean;
  panelId: string;
  titleId: string;
  descriptionId: string;
  draftModel: readonly DraftMultiSortRule[];
  columnOptions: readonly MultiSortColumnOption[];
  focusRequest: MultiSortFocusRequest | null;
  announcement: string;
  hasChanges: boolean;
  hasExternalChange: boolean;
  onClose: () => void;
  onApply: () => void;
  onReload: () => void;
  onAdd: () => void;
  onClear: () => void;
  onFieldChange: (index: number, field: string) => void;
  onDirectionChange: (index: number, direction: MultiSortDirection) => void;
  onMove: (index: number, direction: -1 | 1) => void;
  onRemove: (index: number) => void;
};

const MultiSortPanel = ({
  open,
  panelId,
  titleId,
  descriptionId,
  draftModel,
  columnOptions,
  focusRequest,
  announcement,
  hasChanges,
  hasExternalChange,
  onClose,
  onApply,
  onReload,
  onAdd,
  onClear,
  onFieldChange,
  onDirectionChange,
  onMove,
  onRemove,
}: MultiSortPanelProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const selectedFields = new Set(draftModel.map(({ field }) => field));
  const canAddRule = columnOptions.some(({ field, addable }) => addable && !selectedFields.has(field));

  useEffect(() => {
    if (focusRequest?.ruleId === null) addButtonRef.current?.focus();
  }, [focusRequest]);

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        id: panelId,
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': titleId,
        'aria-describedby': descriptionId,
        sx: isMobile
          ? {
              height: 'min(88dvh, 720px)',
              maxHeight: '88vh',
              borderTopLeftRadius: 3,
              borderTopRightRadius: 3,
              overflow: 'hidden',
              pb: 'env(safe-area-inset-bottom)',
            }
          : {
              width: 480,
              maxWidth: '100vw',
              overflow: 'hidden',
            },
      }}>
      <Box display="flex" flexDirection="column" height="100%" minHeight={0}>
        <Typography
          component="div"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            p: 0,
            m: -1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}>
          {announcement}
        </Typography>

        <Box px={{ xs: 2, sm: 3 }} py={2} display="flex" alignItems="flex-start" gap={1.5}>
          <Box
            display="grid"
            sx={{
              placeItems: 'center',
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 2,
              color: 'primary.main',
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.1),
            }}>
            <SortRoundedIcon />
          </Box>

          <Box flex={1} minWidth={0}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography id={titleId} component="h2" variant="h6" fontWeight={700}>
                <FormattedMessage id="datagrid.multiSort.title" defaultMessage="Sıralamayı Yönet" />
              </Typography>
              <Chip
                size="small"
                color={draftModel.length > 0 ? 'primary' : 'default'}
                variant={draftModel.length > 0 ? 'filled' : 'outlined'}
                label={intl.formatMessage(
                  {
                    id: 'datagrid.multiSort.draftCount',
                    defaultMessage: '{count} ölçüt',
                  },
                  { count: draftModel.length }
                )}
              />
            </Stack>
            <Typography id={descriptionId} variant="body2" color="text.secondary" mt={0.25}>
              <FormattedMessage
                id="datagrid.multiSort.description"
                defaultMessage="Ölçütler yukarıdan aşağıya öncelik sırasıyla uygulanır."
              />
            </Typography>
          </Box>

          <Tooltip title={intl.formatMessage({ id: 'datagrid.multiSort.close', defaultMessage: 'Kapat' })}>
            <IconButton
              edge="end"
              onClick={onClose}
              aria-label={intl.formatMessage({ id: 'datagrid.multiSort.close', defaultMessage: 'Kapat' })}>
              <CloseRoundedIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Divider />

        <Box flex={1} minHeight={0} overflow="auto" px={{ xs: 2, sm: 3 }} py={2.5}>
          {hasExternalChange && (
            <Alert
              severity="warning"
              sx={{ mb: 2, alignItems: 'center' }}
              action={
                <Button color="inherit" size="small" onClick={onReload}>
                  <FormattedMessage id="datagrid.multiSort.reload" defaultMessage="Günceli yükle" />
                </Button>
              }>
              <FormattedMessage
                id="datagrid.multiSort.externalChange"
                defaultMessage="Tablonun sıralaması dışarıdan değişti. Devam etmeden önce güncel sıralamayı yükleyin."
              />
            </Alert>
          )}

          {draftModel.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                mb: 2,
                textAlign: 'center',
                borderStyle: 'dashed',
                borderRadius: 2.5,
                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.06 : 0.025),
              }}>
              <Box
                display="grid"
                sx={{
                  placeItems: 'center',
                  width: 48,
                  height: 48,
                  mx: 'auto',
                  mb: 1.5,
                  borderRadius: '50%',
                  color: 'text.secondary',
                  bgcolor: 'action.hover',
                }}>
                <SortRoundedIcon />
              </Box>
              <Typography fontWeight={700}>
                <FormattedMessage id="datagrid.multiSort.emptyTitle" defaultMessage="Henüz sıralama ölçütü yok" />
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                <FormattedMessage
                  id="datagrid.multiSort.emptyDescription"
                  defaultMessage="Verileri istediğiniz öncelik sırasıyla düzenlemek için bir ölçüt ekleyin."
                />
              </Typography>
            </Paper>
          ) : (
            <Stack
              role="list"
              aria-label={intl.formatMessage({
                id: 'datagrid.multiSort.listLabel',
                defaultMessage: 'Sıralama ölçütleri',
              })}
              spacing={1.5}
              mb={2}>
              {draftModel.map((rule, index) => (
                <MultiSortRuleCard
                  key={rule.id}
                  rule={rule}
                  index={index}
                  totalRules={draftModel.length}
                  columnOptions={columnOptions}
                  selectedFields={selectedFields}
                  disabled={hasExternalChange}
                  focusRequestId={focusRequest?.ruleId === rule.id ? focusRequest.sequence : null}
                  onFieldChange={(field) => onFieldChange(index, field)}
                  onDirectionChange={(direction) => onDirectionChange(index, direction)}
                  onMove={(direction) => onMove(index, direction)}
                  onRemove={() => onRemove(index)}
                />
              ))}
            </Stack>
          )}

          <Button
            ref={addButtonRef}
            fullWidth
            variant="outlined"
            startIcon={<AddRoundedIcon />}
            disabled={!canAddRule || hasExternalChange}
            onClick={onAdd}
            sx={{ minHeight: 44, borderStyle: 'dashed', borderRadius: 2 }}>
            <FormattedMessage id="datagrid.multiSort.add" defaultMessage="Sıralama ölçütü ekle" />
          </Button>

          {!canAddRule && (
            <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={1}>
              <FormattedMessage
                id="datagrid.multiSort.allColumnsUsed"
                defaultMessage="Tüm sıralanabilir sütunlar eklendi."
              />
            </Typography>
          )}
        </Box>

        <Divider />

        <Box px={{ xs: 2, sm: 3 }} py={2} bgcolor="background.paper">
          <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1} justifyContent="space-between">
            <Button
              color="inherit"
              disabled={draftModel.length === 0 || hasExternalChange}
              onClick={onClear}
              sx={{ minHeight: 40 }}>
              <FormattedMessage id="datagrid.multiSort.clearAll" defaultMessage="Tümünü temizle" />
            </Button>

            <Stack direction="row" spacing={1} sx={{ '& > *': { flex: { xs: 1, sm: 'initial' } } }}>
              <Button color="inherit" onClick={onClose} sx={{ minHeight: 40 }}>
                <FormattedMessage id="datagrid.multiSort.cancel" defaultMessage="Vazgeç" />
              </Button>
              <Button
                variant="contained"
                disabled={!hasChanges || hasExternalChange}
                onClick={onApply}
                sx={{
                  minHeight: 40,
                  '&.Mui-disabled': {
                    color: theme.palette.text.disabled,
                    borderColor: theme.palette.divider,
                    backgroundColor: theme.palette.action.disabledBackground,
                    backgroundImage: 'none',
                    boxShadow: 'none',
                    opacity: 1,
                  },
                }}>
                <FormattedMessage id="datagrid.multiSort.apply" defaultMessage="Uygula" />
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
};

export default MultiSortPanel;
